import React, { useRef } from "react";
import { useField } from "formik";
import { Box, Flex, Text, TextField } from "@radix-ui/themes";
import VariableMenu from "./VariableMenu";
import { WorkflowStep } from "@/types/breeze/Workflow";

interface VariableInputFieldProps {
  name: string;
  label?: string;
  placeholder?: string;
  description?: string;
  required?: boolean;
  existingSteps?: WorkflowStep[];
  triggerEntity?: string;
}

export default function VariableInputField({
  name,
  label,
  placeholder,
  description,
  required = false,
  existingSteps = [],
  triggerEntity,
}: VariableInputFieldProps) {
  const [field, meta, helpers] = useField<string>(name);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleInsert = (variableText: string) => {
    helpers.setValue(variableText);

    const inputEl = inputRef.current;
    if (inputEl) {
      setTimeout(() => {
        inputEl.focus();
        inputEl.setSelectionRange(variableText.length, variableText.length);
      }, 50);
    }
  };

  return (
    <Box mb="4" width="100%">
      {label && (
        <Flex justify="between" align="center" mb="1">
          <Text as="label" size="2" weight="medium">
            {label} {required && <Text color="red">*</Text>}
          </Text>
        </Flex>
      )}

      {description && (
        <Text size="1" color="gray" style={{ display: "block", marginBottom: 6 }}>
          {description}
        </Text>
      )}

      <Flex gap="2" align="center">
        <TextField.Root
          ref={inputRef}
          id={name}
          placeholder={placeholder}
          value={field.value || ""}
          onChange={(e) => helpers.setValue(e.target.value)}
          style={{ width: "100%", fontFamily: "monospace" }}
        />
        <VariableMenu
          existingSteps={existingSteps}
          triggerEntity={triggerEntity}
          onInsert={handleInsert}
          showLoopContext={false}
        />
      </Flex>

      {meta.touched && meta.error && (
        <Text size="1" color="red" mt="1" style={{ display: "block" }}>
          {meta.error}
        </Text>
      )}
    </Box>
  );
}
